import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Plus, Loader2, Megaphone, Edit2, Trash2 } from 'lucide-react';

const EMPTY = {
  message: '',
  background_color: '#3B82F6',
  text_color: '#FFFFFF',
  link_url: '',
  link_text: '',
  placement: 'above_header',
  visibility: 'everyone',
  is_dismissible: true,
  start_date: '',
  end_date: '',
  priority: 0,
};

export const AnnouncementsSection = ({ api }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const [createAnnouncementDialog, setCreateAnnouncementDialog] = useState(false);
  const [editAnnouncementDialog, setEditAnnouncementDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState(EMPTY);

  const fetchAnnouncements = useCallback(async () => {
    setAnnouncementLoading(true);
    try {
      const response = await api.get('/announcements/all');
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setAnnouncementLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleCreateAnnouncement = async () => {
    try {
      const annData = {
        ...newAnnouncement,
        start_date: newAnnouncement.start_date || null,
        end_date: newAnnouncement.end_date || null,
      };
      await api.post('/announcements', annData);
      toast.success('Announcement created!');
      setCreateAnnouncementDialog(false);
      setNewAnnouncement(EMPTY);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const handleUpdateAnnouncement = async () => {
    if (!editingAnnouncement) return;
    try {
      await api.put(`/announcements/${editingAnnouncement.id}`, editingAnnouncement);
      toast.success('Announcement updated!');
      setEditAnnouncementDialog(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch {
      toast.error('Failed to delete announcement');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center pr-14 md:pr-40">
        <h1 className="text-2xl font-bold text-slate-800">Announcement Banners</h1>
        <Button onClick={() => setCreateAnnouncementDialog(true)} data-testid="create-announcement-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Announcement
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          {announcementLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No announcements yet</p>
              <Button className="mt-4" onClick={() => setCreateAnnouncementDialog(true)}>
                Create First Announcement
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((ann) => (
                <Card key={ann.id} className="overflow-hidden">
                  <div
                    className="px-4 py-2 text-sm font-medium"
                    style={{ backgroundColor: ann.background_color, color: ann.text_color }}
                  >
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4" />
                      <span>{ann.message}</span>
                      {ann.link_url && <span className="underline ml-2">{ann.link_text || 'Learn more'}</span>}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded text-xs',
                              ann.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                            )}
                          >
                            {ann.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                            {ann.placement === 'above_header' ? 'Above Header' : 'Below Header'}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                            {ann.visibility === 'everyone' ? 'Everyone' : 'Logged-in Only'}
                          </span>
                          {ann.is_dismissible && (
                            <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Dismissible</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          Priority: {ann.priority} | Created: {new Date(ann.created_at).toLocaleDateString()}
                          {ann.start_date && ` | Starts: ${new Date(ann.start_date).toLocaleDateString()}`}
                          {ann.end_date && ` | Ends: ${new Date(ann.end_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingAnnouncement({ ...ann });
                            setEditAnnouncementDialog(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteAnnouncement(ann.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createAnnouncementDialog} onOpenChange={setCreateAnnouncementDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Message</Label>
              <Textarea
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                placeholder="e.g., 🎉 Special offer! Get 20% off with code SAVE20"
                data-testid="announcement-message-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={newAnnouncement.background_color}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, background_color: e.target.value })}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={newAnnouncement.background_color}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, background_color: e.target.value })}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>
              <div>
                <Label>Text Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={newAnnouncement.text_color}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, text_color: e.target.value })}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={newAnnouncement.text_color}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, text_color: e.target.value })}
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Link URL (Optional)</Label>
                <Input
                  value={newAnnouncement.link_url}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, link_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Link Text</Label>
                <Input
                  value={newAnnouncement.link_text}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, link_text: e.target.value })}
                  placeholder="Learn more"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Placement</Label>
                <Select
                  value={newAnnouncement.placement}
                  onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, placement: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="above_header">Above Header</SelectItem>
                    <SelectItem value="below_header">Below Header</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visibility</Label>
                <Select
                  value={newAnnouncement.visibility}
                  onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, visibility: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="everyone">Everyone</SelectItem>
                    <SelectItem value="logged_in_only">Logged-in Users Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={newAnnouncement.start_date}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={newAnnouncement.end_date}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={newAnnouncement.is_dismissible}
                  onCheckedChange={(v) => setNewAnnouncement({ ...newAnnouncement, is_dismissible: v })}
                />
                <Label>Allow users to dismiss</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label>Priority:</Label>
                <Input
                  type="number"
                  value={newAnnouncement.priority}
                  onChange={(e) =>
                    setNewAnnouncement({ ...newAnnouncement, priority: parseInt(e.target.value) || 0 })
                  }
                  className="w-20"
                />
              </div>
            </div>
            <div>
              <Label>Preview:</Label>
              <div
                className="mt-2 px-4 py-2 text-sm font-medium rounded"
                style={{ backgroundColor: newAnnouncement.background_color, color: newAnnouncement.text_color }}
              >
                <div className="flex items-center gap-2">
                  <Megaphone className="w-4 h-4" />
                  <span>{newAnnouncement.message || 'Your announcement message here...'}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateAnnouncementDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAnnouncement} disabled={!newAnnouncement.message}>
              Create Announcement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editAnnouncementDialog} onOpenChange={setEditAnnouncementDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
          </DialogHeader>
          {editingAnnouncement && (
            <div className="space-y-4">
              <div>
                <Label>Message</Label>
                <Textarea
                  value={editingAnnouncement.message}
                  onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, message: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Background Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editingAnnouncement.background_color}
                      onChange={(e) =>
                        setEditingAnnouncement({ ...editingAnnouncement, background_color: e.target.value })
                      }
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editingAnnouncement.background_color}
                      onChange={(e) =>
                        setEditingAnnouncement({ ...editingAnnouncement, background_color: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Text Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editingAnnouncement.text_color}
                      onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, text_color: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={editingAnnouncement.text_color}
                      onChange={(e) => setEditingAnnouncement({ ...editingAnnouncement, text_color: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Placement</Label>
                  <Select
                    value={editingAnnouncement.placement}
                    onValueChange={(v) => setEditingAnnouncement({ ...editingAnnouncement, placement: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="above_header">Above Header</SelectItem>
                      <SelectItem value="below_header">Below Header</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select
                    value={editingAnnouncement.visibility}
                    onValueChange={(v) => setEditingAnnouncement({ ...editingAnnouncement, visibility: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="everyone">Everyone</SelectItem>
                      <SelectItem value="logged_in_only">Logged-in Users Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingAnnouncement.is_dismissible}
                    onCheckedChange={(v) => setEditingAnnouncement({ ...editingAnnouncement, is_dismissible: v })}
                  />
                  <Label>Dismissible</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingAnnouncement.is_active}
                    onCheckedChange={(v) => setEditingAnnouncement({ ...editingAnnouncement, is_active: v })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
              <div>
                <Label>Preview:</Label>
                <div
                  className="mt-2 px-4 py-2 text-sm font-medium rounded"
                  style={{
                    backgroundColor: editingAnnouncement.background_color,
                    color: editingAnnouncement.text_color,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4" />
                    <span>{editingAnnouncement.message}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAnnouncementDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAnnouncement}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
